import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PageTitleService } from 'app/services/page-title/page-title.service';
import { environment } from 'environments/environment';
import { debounceTime, startWith } from 'rxjs/operators';
import { CodaltComponent } from '../codalt.component';
import { LocalStorage } from '../local-storage.class';
import { AuthService } from '../services/auth/auth.service';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { LoginFormState } from './login-form-state.enum';

type UserType = 'consumer' | 'client' | 'employee';

interface ConsumerProject {
    id: number;
    name: string;
    customer_id: number;
    customer_name: string;
}

interface LoginFormControls {
    email: FormControl<string | null>;
    password: FormControl<string | null>;
}

interface LoginErrorResponse {
    message?: string;
    status?: number;
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false,
})
export class LoginComponent extends CodaltComponent implements OnInit, OnDestroy {
    errorMessage: string | null = null;

    LoginFormState = LoginFormState;

    usertype: UserType | null = null;
    register: boolean | null = LocalStorage.getRegister();
    customerProject: ConsumerProject | null = null;
    customerProjects: ConsumerProject[] = [];

    loginFormState: LoginFormState = LoginFormState.FillIn;

    loginForm = new FormGroup<LoginFormControls>({
        email: new FormControl<string | null>(LocalStorage.getMail(), [Validators.required, Validators.email]),
        password: new FormControl(),
    });

    constructor(
        private authService: AuthService,
        private router: Router,
        private confirmDialog: ConfirmDialogService,
        private customerProjectService: CustomerProjectService,
        private pageTitleService: PageTitleService
    ) {
        super();
        this.subscriptions.add(
            this.loginForm.valueChanges.subscribe(() => {
                LocalStorage.setMail(this.loginForm.value.email ?? '');
                this.errorMessage = null;
            })
        );
    }

    ngOnInit() {
        this.pageTitleService.setTitle('Inloggen');

        this.customerProjectService
            .getConsumerProjects()
            .subscribe(response => (this.customerProjects = response.data));
        this.subscriptions.add(
            this.loginForm.controls.email.valueChanges
                .pipe(debounceTime(200), startWith(this.loginForm.controls.email.value))
                .subscribe(() => this.checkLoginType())
        );
    }

    normalizeEmail(): string {
        const email = this.loginForm.controls.email.value?.trim() ?? '';
        this.loginForm.controls.email.setValue(email, { emitEvent: false });
        return email;
    }

    loginWithPassword() {
        this.authService.login(this.loginForm.value.email, this.loginForm.value.password).subscribe(
            authResponse => {
                LocalStorage.setUserToken(authResponse.access_token);
                LocalStorage.setUser(authResponse.user);
                this.router.navigate([''], { replaceUrl: true });
            },
            failReason => {
                this.errorMessage = failReason.message;
                if (!this.errorMessage) {
                    this.errorMessage = 'Kon de server niet bereiken';
                }
                this.loginFormState = LoginFormState.FillInPassword;
            }
        );
    }

    login(): void {
        const email = this.normalizeEmail();

        if (this.loginForm.invalid) {
            this.loginForm.markAllAsTouched();
            return;
        }

        if (![LoginFormState.FillIn, LoginFormState.Error, LoginFormState.MsLogin].includes(this.loginFormState)) {
            return;
        }

        this.loginFormState = LoginFormState.RequestingCode;
        this.subscriptions.add(
            this.authService.loginEmail(email).subscribe(
                () =>
                    this.router.navigate(['/', this.Routenames.login, this.Routenames.code], {
                        queryParams: { email },
                        replaceUrl: true,
                    }),
                (failReason: LoginErrorResponse) => {
                    if (failReason?.status === 404) {
                        this.confirmDialog
                            .confirm(
                                'Je hebt nog geen account',
                                `Er bestaat geen account voor het e-mailadres <b>${email}</b>.<br> Probeer het opnieuw of vraag een account aan.`,
                                'Sluiten'
                            )
                            .then(() => (this.loginFormState = LoginFormState.FillIn))
                            .catch(() => {});
                    } else {
                        this.errorMessage = failReason.message;
                        if (!this.errorMessage) {
                            this.errorMessage = 'Kon de server niet bereiken';
                        }
                        this.loginFormState = LoginFormState.Error;
                    }
                }
            )
        );
    }

    changeRegister(): void {
        this.usertype = null;
        this.customerProject = null;

        if (this.register !== null) {
            LocalStorage.setRegister(this.register);
        }
    }

    loginWithMicrosoft(): void {
        this.subscriptions.add(
            this.authService.loginWithMicrosoft().subscribe(
                data => {
                    if (data.auth_url) {
                        window.location.href = data.auth_url;
                    }
                },
                () => (this.errorMessage = 'Microsoft login is momenteel niet beschikbaar')
            )
        );
    }

    checkLoginType(): void {
        if (!this.loginForm.controls.email.valid) {
            return;
        }

        this.subscriptions.add(
            this.authService.loginType(this.loginForm.controls.email.value ?? '').subscribe(type => {
                switch (type?.data?.loginType) {
                    case 'microsoft':
                        this.loginFormState = LoginFormState.MsLogin;
                        break;
                    case 'password':
                        this.loginFormState = LoginFormState.FillInPassword;
                        break;
                    default:
                        this.loginFormState = LoginFormState.FillIn;
                }
            })
        );
    }

    public forgotPassword() {
        window.location.href = `${environment.feEndpoint}wachtwoord-vergeten?${new URLSearchParams({ email: this.loginForm.value.email ?? '' })}`;
    }
}
